/**
 * Priority is how much it matters, not how soon it is. The Notifications
 * widget shows it as a left edge (coral for Critical, white for High) and a
 * word in the meta line; time is shown separately in the "when" column.
 *
 * Mapped from the fields notificationPolicy.ts already derives:
 * - `urgency` (1–5, clamped like the policy's `urgencyScore`). Producers set 5
 *   for things that break imminently (e.g. a calendar conflict that starts
 *   soon, `quietConflictDetection` "interrupt") → Critical.
 * - `urgency` 4 (HIGH-relevance insights, non-imminent conflicts) → High.
 * - attention candidates' `importanceScore` 5 → High even when the item's own
 *   urgency was lowered for presentation.
 * - notification types whose cost of missing is someone else's time or a
 *   commitment (conflicts, deadlines, replies owed, obligations) → at least High.
 * Everything else is Normal. `surfacePriority` is deliberately not used: it
 * encodes feed ordering bands (reminders rank 100) and mixes in time.
 */

export type NotificationPriority = "critical" | "high" | "normal";

export type PrioritizableNotification = {
  urgency?: unknown;
  notificationType?: unknown;
  source?: unknown;
  categories?: unknown;
  attentionCandidates?: unknown;
};

export const PRIORITY_RANK: Record<NotificationPriority, number> = { critical: 0, high: 1, normal: 2 };

export const PRIORITY_LABEL: Record<NotificationPriority, string | null> = {
  critical: "Critical",
  high: "High",
  normal: null,
};

const HIGH_STAKES_KINDS = new Set([
  "conflict_alert",
  "deadline_alert",
  "reply_attention",
  "obligation_follow_up",
  "counterparty_commitment",
]);

const score = (value: unknown): number | null => {
  const numeric = typeof value === "string" && value.trim() ? Number(value) : value;
  if (typeof numeric !== "number" || !Number.isFinite(numeric)) return null;
  return Math.min(Math.max(Math.round(numeric), 1), 5);
};

const text = (value: unknown) => (typeof value === "string" ? value.trim().toLowerCase() : "");

const liveCandidateImportance = (candidates: unknown): number => {
  if (!Array.isArray(candidates)) return 0;
  let best = 0;
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const { status, importanceScore } = candidate as { status?: unknown; importanceScore?: unknown };
    if (status === "resolved" || status === "suppressed") continue;
    best = Math.max(best, score(importanceScore) ?? 0);
  }
  return best;
};

export function getNotificationPriority(item: PrioritizableNotification): NotificationPriority {
  const urgency = score(item.urgency) ?? 2;
  if (urgency >= 5) return "critical";

  const categories = Array.isArray(item.categories) ? item.categories.map(text) : [];
  const kinds = [text(item.notificationType), text(item.source), ...categories];
  if (
    urgency >= 4 ||
    liveCandidateImportance(item.attentionCandidates) >= 5 ||
    kinds.some((kind) => HIGH_STAKES_KINDS.has(kind))
  ) {
    return "high";
  }
  return "normal";
}
